import { escapeXml, extractErrorMessage, sendIntacctBatch } from './core';

// ============ JOB COST SETUP CONFIGURATION ============
const JOB_COST_REQUIRED_COLUMNS = ['GLENTRY_PROJECTID', 'GLENTRY_TASKID', 'GLENTRY_COSTTYPEID'];
const STANDARD_TASK_ERROR_PATTERN = /standard cost code .* does not exist/i;

// ============ DETECTION ============
export const isJobCostSetupFlow = (headers) => {
  if (!headers || !Array.isArray(headers)) return false;
  const upperHeaders = headers.map(h => String(h).toUpperCase().trim());
  return JOB_COST_REQUIRED_COLUMNS.every(col => upperHeaders.includes(col));
};

// ============ FUNCTIONS ============
export const getJobCostSetupSummary = (records) => {
  const validRecords = records.filter(rec => {
    const projectId = rec.GLENTRY_PROJECTID || '';
    const taskId = rec.GLENTRY_TASKID || '';
    const costTypeId = rec.GLENTRY_COSTTYPEID || '';
    return !String(projectId).startsWith('Field name') &&
           !String(taskId).startsWith('Field name') &&
           !String(costTypeId).startsWith('Field name');
  });
  
  const taskSet = new Set();
  const tasks = [];
  validRecords.forEach(rec => {
    const projectId = String(rec.GLENTRY_PROJECTID || '').trim();
    const taskId = String(rec.GLENTRY_TASKID || '').trim();
    if (projectId && taskId) {
      const key = `${projectId}|||${taskId}`;
      if (!taskSet.has(key)) {
        taskSet.add(key);
        tasks.push({ projectId, taskId });
      }
    }
  });
  
  const costTypeSet = new Set();
  const costTypes = [];
  validRecords.forEach(rec => {
    const projectId = String(rec.GLENTRY_PROJECTID || '').trim();
    const taskId = String(rec.GLENTRY_TASKID || '').trim();
    const costTypeId = String(rec.GLENTRY_COSTTYPEID || '').trim();
    if (projectId && taskId && costTypeId) {
      const key = `${projectId}|||${taskId}|||${costTypeId}`;
      if (!costTypeSet.has(key)) {
        costTypeSet.add(key);
        costTypes.push({ projectId, taskId, costTypeId });
      }
    }
  });
  
  return {
    totalRecords: validRecords.length,
    tasks, costTypes,
    totalTasks: tasks.length,
    totalCostTypes: costTypes.length
  };
};

export const buildJobCostPreviewXml = (records) => {
  const summary = getJobCostSetupSummary(records);
  
  let preview = '<!-- STEP 1: Create TASKs with STANDARDTASKID -->\n';
  if (summary.tasks.length > 0) {
    const sampleTask = summary.tasks[0];
    preview += `<function controlid="${escapeXml(sampleTask.projectId)}-${escapeXml(sampleTask.taskId)}">
  <create>
    <TASK>
      <PROJECTID>${escapeXml(sampleTask.projectId)}</PROJECTID>
      <TASKID>${escapeXml(sampleTask.taskId)}</TASKID>
      <STANDARDTASKID>${escapeXml(sampleTask.taskId)}</STANDARDTASKID>
    </TASK>
  </create>
</function>`;
    if (summary.tasks.length > 1) {
      preview += `\n<!-- ... and ${summary.tasks.length - 1} more TASK(s) ... -->`;
    }
  }
  
  preview += '\n\n<!-- STEP 2 (if needed): Retry failed TASKs without STANDARDTASKID -->\n';
  if (summary.tasks.length > 0) {
    const sampleTask = summary.tasks[0];
    preview += `<function controlid="${escapeXml(sampleTask.projectId)}-${escapeXml(sampleTask.taskId)}-retry">
  <create>
    <TASK>
      <PROJECTID>${escapeXml(sampleTask.projectId)}</PROJECTID>
      <TASKID>${escapeXml(sampleTask.taskId)}</TASKID>
      <NAME>${escapeXml(sampleTask.taskId)}</NAME>
      <TASKSTATUS>In Progress</TASKSTATUS>
    </TASK>
  </create>
</function>`;
  }
  
  preview += '\n\n<!-- STEP 3: Create COSTTYPEs -->\n';
  if (summary.costTypes.length > 0) {
    const sampleCostType = summary.costTypes[0];
    preview += `<function controlid="${escapeXml(sampleCostType.projectId)}-${escapeXml(sampleCostType.taskId)}-${escapeXml(sampleCostType.costTypeId)}">
  <create>
    <COSTTYPE>
      <PROJECTID>${escapeXml(sampleCostType.projectId)}</PROJECTID>
      <TASKID>${escapeXml(sampleCostType.taskId)}</TASKID>
      <STANDARDCOSTTYPEID>${escapeXml(sampleCostType.costTypeId)}</STANDARDCOSTTYPEID>
    </COSTTYPE>
  </create>
</function>`;
    if (summary.costTypes.length > 1) {
      preview += `\n<!-- ... and ${summary.costTypes.length - 1} more COSTTYPE(s) ... -->`;
    }
  }
  
  return preview;
};

export const executeJobCostSetup = async (config, records, onProgress) => {
  const summary = getJobCostSetupSummary(records);
  const results = {
    tasks: [],
    costTypes: [],
    summary: {
      tasksAttempted: summary.totalTasks, tasksSucceeded: 0, tasksRetried: 0,
      tasksRetriedSucceeded: 0, tasksFailed: 0,
      costTypesAttempted: summary.totalCostTypes, costTypesSucceeded: 0, costTypesFailed: 0
    }
  };
  
  // STEP 1: Create TASKs with STANDARDTASKID
  if (onProgress) onProgress('tasks-standard', 0, summary.totalTasks);
  
  const taskFunctions = summary.tasks.map(task => {
    const controlId = `${task.projectId}-${task.taskId}`;
    return `<function controlid="${escapeXml(controlId)}"><create><TASK><PROJECTID>${escapeXml(task.projectId)}</PROJECTID><TASKID>${escapeXml(task.taskId)}</TASKID><STANDARDTASKID>${escapeXml(task.taskId)}</STANDARDTASKID></TASK></create></function>`;
  });
  
  const taskResults = await sendIntacctBatch(config, taskFunctions, 'TASK');
  
  const tasksToRetry = [];
  summary.tasks.forEach((task, idx) => {
    const res = taskResults[idx] || { status: 'error', errormessage: 'No response' };
    const errorMsg = extractErrorMessage(res);
    
    if (res.status === 'success') {
      results.tasks.push({ ...task, status: 'success', message: 'Success', retried: false });
      results.summary.tasksSucceeded++;
    } else if (errorMsg && STANDARD_TASK_ERROR_PATTERN.test(errorMsg)) {
      tasksToRetry.push(task);
      results.tasks.push({ ...task, status: 'pending-retry', message: errorMsg, retried: false });
    } else {
      results.tasks.push({ ...task, status: 'failed', message: errorMsg, retried: false });
      results.summary.tasksFailed++;
    }
  });
  
  if (onProgress) onProgress('tasks-standard', summary.totalTasks, summary.totalTasks);
  
  // STEP 2: Retry failed TASKs without STANDARDTASKID
  if (tasksToRetry.length > 0) {
    if (onProgress) onProgress('tasks-retry', 0, tasksToRetry.length);
    results.summary.tasksRetried = tasksToRetry.length;
    
    const retryFunctions = tasksToRetry.map(task => {
      const controlId = `${task.projectId}-${task.taskId}-retry`;
      return `<function controlid="${escapeXml(controlId)}"><create><TASK><PROJECTID>${escapeXml(task.projectId)}</PROJECTID><TASKID>${escapeXml(task.taskId)}</TASKID><NAME>${escapeXml(task.taskId)}</NAME><TASKSTATUS>In Progress</TASKSTATUS></TASK></create></function>`;
    });
    
    const retryResults = await sendIntacctBatch(config, retryFunctions, 'TASK');
    
    tasksToRetry.forEach((task, idx) => {
      const res = retryResults[idx] || { status: 'error', errormessage: 'No response' };
      const errorMsg = extractErrorMessage(res);
      
      const existingIdx = results.tasks.findIndex(t => 
        t.projectId === task.projectId && t.taskId === task.taskId && t.status === 'pending-retry'
      );
      
      if (existingIdx !== -1) {
        if (res.status === 'success') {
          results.tasks[existingIdx] = { ...task, status: 'success', message: 'Success (retried without standard)', retried: true };
          results.summary.tasksRetriedSucceeded++;
        } else {
          results.tasks[existingIdx] = { ...task, status: 'failed', message: errorMsg, retried: true };
          results.summary.tasksFailed++;
        }
      }
    });
    
    if (onProgress) onProgress('tasks-retry', tasksToRetry.length, tasksToRetry.length);
  }
  
  // STEP 3: Create COSTTYPEs
  if (summary.totalCostTypes > 0) {
    if (onProgress) onProgress('costtypes', 0, summary.totalCostTypes);
    
    const costTypeFunctions = summary.costTypes.map(ct => {
      const controlId = `${ct.projectId}-${ct.taskId}-${ct.costTypeId}`;
      return `<function controlid="${escapeXml(controlId)}"><create><COSTTYPE><PROJECTID>${escapeXml(ct.projectId)}</PROJECTID><TASKID>${escapeXml(ct.taskId)}</TASKID><STANDARDCOSTTYPEID>${escapeXml(ct.costTypeId)}</STANDARDCOSTTYPEID></COSTTYPE></create></function>`;
    });
    
    const costTypeResults = await sendIntacctBatch(config, costTypeFunctions, 'COSTTYPE');
    
    summary.costTypes.forEach((ct, idx) => {
      const res = costTypeResults[idx] || { status: 'error', errormessage: 'No response' };
      const errorMsg = extractErrorMessage(res);
      
      if (res.status === 'success') {
        results.costTypes.push({ ...ct, status: 'success', message: 'Success' });
        results.summary.costTypesSucceeded++;
      } else {
        results.costTypes.push({ ...ct, status: 'failed', message: errorMsg });
        results.summary.costTypesFailed++;
      }
    });
    
    if (onProgress) onProgress('costtypes', summary.totalCostTypes, summary.totalCostTypes);
  }
  
  return results;
};
